import type { FormEvent } from "react";
import { useCallback, useState } from "react";
import { createUseStyles } from "react-jss";
import type { Boat, NewAvailabilityWindow } from "../../../../common/src";
import {
  newAvailabilityWindowSchema,
  todayInTimeZone,
} from "../../../../common/src";
import { useAction } from "../../hooks/useAction";
import { useAsyncData } from "../../hooks/useAsyncData";
import type { VetBoatsTheme } from "../../theme";
import { trpc } from "../../trpc";
import { toFieldErrors } from "../../utils/fieldErrors";
import { formatDays, formatRange } from "../../utils/format";
import { Badge } from "../shared/Badge";
import { Button } from "../shared/Button";
import { Card } from "../shared/Card";
import { ConfirmButton } from "../shared/ConfirmButton";
import { Field } from "../shared/Field";
import { Notice } from "../shared/Notice";
import { EmptyState, LoadError, Loading } from "../shared/StatusViews";
import { useFormStyles, useLayoutStyles } from "../shared/styles";
import { navigate } from "../../hooks/useRoute";

const useStyles = createUseStyles((theme: VetBoatsTheme) => ({
  layout: {
    display: "grid",
    gridTemplateColumns: "minmax(280px, 1fr) minmax(0, 1.5fr)",
    gap: 20,
    alignItems: "start",
    "@media (max-width: 900px)": { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  list: { listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 },
  item: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    padding: "10px 12px",
    border: `1px solid ${theme.color.border}`,
    borderRadius: 6,
  },
  meta: {
    color: theme.color.muted,
    fontSize: 14,
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
  },
}));

const WindowsEditor = ({ boat }: { boat: Boat }) => {
  const classes = useStyles();
  const form = useFormStyles();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [deletingId, setDeletingId] = useState<number | undefined>(undefined);

  const load = useCallback(
    (signal: AbortSignal) =>
      trpc.availability.list.query({ boatId: boat.id }, { signal }),
    [boat.id]
  );
  const windows = useAsyncData(load);
  const create = useAction(
    useCallback(
      (input: NewAvailabilityWindow) => trpc.availability.create.mutate(input),
      []
    )
  );
  const remove = useAction(
    useCallback((id: number) => trpc.availability.delete.mutate({ id }), [])
  );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const parsed = newAvailabilityWindowSchema.safeParse({
      boatId: boat.id,
      startDate,
      endDate,
      note,
    });
    if (!parsed.success) {
      setErrors(toFieldErrors(parsed.error));
      return;
    }
    setErrors({});
    if (await create.run(parsed.data)) {
      setStartDate("");
      setEndDate("");
      setNote("");
      windows.reload();
    }
  };

  const onDelete = async (id: number) => {
    setDeletingId(id);
    await remove.run(id);
    setDeletingId(undefined);
    windows.reload();
  };

  const today = todayInTimeZone(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  return (
    <div className={classes.layout}>
      <Card title="Open dates">
        <p className={form.intro}>
          {boat.name} can only be booked on days inside an availability window.
          Back-to-back windows count as one continuous stretch.
        </p>
        {create.error && (
          <Notice kind="error" onDismiss={create.clearError}>
            {create.error}
          </Notice>
        )}
        <form onSubmit={(e) => void submit(e)} noValidate>
          <div className={form.grid}>
            <Field label="From" htmlFor="win-start" error={errors.startDate}>
              <input
                id="win-start"
                type="date"
                className={form.control}
                value={startDate}
                min={today}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Field>
            <Field label="Through" htmlFor="win-end" error={errors.endDate}>
              <input
                id="win-end"
                type="date"
                className={form.control}
                value={endDate}
                min={startDate || today}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Field>
          </div>
          <div className={form.spaced}>
            <Field
              label="Note"
              htmlFor="win-note"
              hint="Optional, e.g. “Summer season”"
              error={errors.note}
            >
              <input
                id="win-note"
                className={form.control}
                value={note}
                maxLength={200}
                onChange={(e) => setNote(e.target.value)}
              />
            </Field>
          </div>
          <div className={form.actions}>
            <Button type="submit" disabled={create.pending}>
              {create.pending ? "Adding…" : "Add window"}
            </Button>
          </div>
        </form>
      </Card>
      <Card title="Availability windows">
        {remove.error && (
          <Notice kind="error" onDismiss={remove.clearError}>
            {remove.error}
          </Notice>
        )}
        {windows.error && (
          <LoadError message={windows.error} onRetry={windows.reload} />
        )}
        {windows.loading && !windows.data && <Loading />}
        {windows.data?.length === 0 && (
          <EmptyState title="No open dates">
            Add a window so veterans can start booking.
          </EmptyState>
        )}
        {windows.data && windows.data.length > 0 && (
          <ul className={classes.list}>
            {windows.data.map((w) => (
              <li key={w.id} className={classes.item}>
                <div>
                  <strong>{formatRange(w)}</strong>
                  <div className={classes.meta}>
                    <span>{formatDays(w)}</span>
                    {w.note && <span>· {w.note}</span>}
                    {w.endDate < today && <Badge tone="neutral">Past</Badge>}
                    {w.startDate <= today && w.endDate >= today && (
                      <Badge tone="success">Current</Badge>
                    )}
                  </div>
                </div>
                <ConfirmButton
                  label="Remove"
                  confirmLabel="Remove window"
                  pending={deletingId === w.id}
                  onConfirm={() => void onDelete(w.id)}
                />
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
};

/** Admin availability editor; the boat comes from the URL, defaulting to the first boat. */
export const AvailabilityPage = ({
  boatId,
}: {
  boatId: number | undefined;
}) => {
  const form = useFormStyles();
  const layout = useLayoutStyles();
  const loadBoats = useCallback(
    (signal: AbortSignal) => trpc.boat.list.query(undefined, { signal }),
    []
  );
  const boats = useAsyncData(loadBoats);

  if (boats.error) {
    return <LoadError message={boats.error} onRetry={boats.reload} />;
  }
  if (boats.loading && !boats.data) {
    return <Loading label="Loading boats…" />;
  }
  const list = boats.data ?? [];
  const boat = list.find((b) => b.id === boatId) ?? list[0];
  if (!boat) {
    return (
      <EmptyState
        title="Add a boat first"
        action={
          <Button
            onClick={() => navigate({ page: "adminBoat", boatId: "new" })}
          >
            Add boat
          </Button>
        }
      />
    );
  }
  return (
    <div className={layout.sections}>
      {list.length > 1 && (
        <Card>
          <Field label="Boat" htmlFor="availability-boat">
            <select
              id="availability-boat"
              className={form.control}
              value={boat.id}
              onChange={(e) =>
                navigate({
                  page: "availability",
                  boatId: Number(e.target.value),
                })
              }
            >
              {list.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                  {b.isActive ? "" : " (inactive)"}
                </option>
              ))}
            </select>
          </Field>
        </Card>
      )}
      {/* Remount per boat so the add-window form starts fresh. */}
      <WindowsEditor key={boat.id} boat={boat} />
    </div>
  );
};
